import {AbstractControl, AbstractControlOptions, AsyncValidator, AsyncValidatorFn, FormControlOptions, ValidationErrors, ValidatorFn} from '@angular/forms';
import {Observable} from 'rxjs';
import {OneOrArrayOf} from '@aviary/store/utility-type.helpers';
import {isObject} from '@aviary/store/common/typing.helpers';
import {Action, Store} from '@ngrx/store';
import {MxSelector} from '@aviary/store/store.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- We're extending an Angular reactive forms type that uses any.
export interface ExtendedAbstractControl<TValue = any, TRawValue extends TValue = TValue> extends AbstractControl<TValue, TRawValue> {
  readonly uuid: string;
  shouldRender: boolean;
}

export type AsyncValidateFunction = (control: AbstractControl) => Observable<ValidationErrors | null>;

export type ExtendedAsyncValidateFunction = (control: ExtendedAbstractControl) => Observable<ValidationErrors | null>;

export interface ExtendedAsyncValidator extends AsyncValidator {
  validate(control: ExtendedAbstractControl): Observable<ValidationErrors | null>;
}

export type ObjectValidators<T extends object> = {
  [K in keyof T]?: T[K] extends object
    ? OneOrArrayOf<ValidatorFn> | ExtendedFormControlOptions | ObjectValidators<T[K]>
    : OneOrArrayOf<ValidatorFn> | ExtendedFormControlOptions;
};

// Export this here to limit Sonar's "non-ASCII characters" warning to one place -- Angular uses this character to make it clear that this is an internal type.
export {ɵElement as AngularInternalElement} from '@angular/forms';

export type ExtendedAsyncValidatorFn = (control: ExtendedAbstractControl) => Observable<ValidationErrors | null>;

export interface ExtendedAsyncValidatorConfig {
  readonly store: Store;
  readonly validationActionCreator: ExtendedAsyncValidationActionCreator;
  readonly validitySelector: MxSelector<boolean | 'UNKNOWN'>;
  readonly validationErrorKey: string;
  readonly overrideErrorMessage?: string;
  readonly validateOnlyIf?: ExtendedAsyncValidationEnablementFunction;
}

export type ExtendedAsyncValidationActionCreator = (valueToValidate: string) => Action;

export type ExtendedAsyncValidationEnablementFunction = (control: ExtendedAbstractControl) => boolean;

export type ExtendedAbstractControlOptions = Omit<AbstractControlOptions, 'asyncValidators'> & {
  asyncValidators?: OneOrArrayOf<ExtendedAsyncValidatorFn> | null;
};

export type ExtendedFormControlOptions = ExtendedAbstractControlOptions & {
  nonNullable?: boolean;
};

export function isExtendedFormControlOptions(obj: unknown): obj is ExtendedFormControlOptions {
  return isObject(obj) && ('validators' in obj || 'asyncValidators' in obj);
}

export type AngularValidator<T extends AbstractControlOptions | FormControlOptions> = OneOrArrayOf<ValidatorFn> | T;
export type AngularAsyncValidator = OneOrArrayOf<AsyncValidatorFn>;

export type ExtendedAngularValidator<T extends ExtendedAbstractControlOptions | ExtendedFormControlOptions> = OneOrArrayOf<ValidatorFn> | T;
export type ExtendedAngularAsyncValidator = OneOrArrayOf<ExtendedAsyncValidatorFn>;

export type AngularFormHooks = 'change' | 'blur' | 'submit';

export type ValidatorOptions = [ValidatorFn[] | undefined, AsyncValidatorFn | undefined, AngularFormHooks | undefined];

export type AngularEmissionOptions = { onlySelf?: boolean; emitEvent?: boolean };
